import React, { useState, useCallback,useEffect } from "react";
import { FaFileCsv, FaFileExcel, FaUpload, FaTimes, FaEdit, FaTrash, FaSave, FaTimesCircle, FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { RiResetLeftLine } from "react-icons/ri";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import { getLeadExcelTemplate, getLeadCSVTemplate, uploadLeadExcel, uploadLeadCSV } from "../../redux/services/leadServices/Lead.Services";
import { useDispatch,useSelector  } from "react-redux";
import { closeModal } from "../../redux/features/modalSlice";
import { triggerRefresh } from "../../redux/features/refreshSlice";
const UploadPreviewModal = () => {
      const dispatch = useDispatch();
 const handleClose = () => {
  dispatch(closeModal());
};
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewDataAll, setPreviewDataAll] = useState([]);
  const [previewColumns, setPreviewColumns] = useState([]);
  const [originalPreviewData, setOriginalPreviewData] = useState([]);
  const [previewCurrentPage, setPreviewCurrentPage] = useState(1);
  const [editingRowIndex, setEditingRowIndex] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const previewRowsPerPage = 10;


  const downloadCSVTemplate = useCallback(async () => {
    try {
      const blob = await getLeadCSVTemplate();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "lead_template.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      Swal.fire("Error", "Failed to download CSV template.", "error");
    }
  }, []);

  const downloadExcelTemplate = useCallback(async () => {
    try {
      const response = await getLeadExcelTemplate();
      const blob = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "lead_template.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      Swal.fire("Error", error?.response?.data?.message || "Excel download failed", "error");
    }
  }, []);

  const handleFileSelect = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const fileExt = file.name.split(".").pop().toLowerCase();
    if (fileExt !== "csv" && fileExt !== "xlsx") {
      Swal.fire({ icon: "error", title: "Invalid File", text: "Please select a CSV or Excel (.xlsx) file." });
      event.target.value = "";
      return;
    }

    setSelectedFile(file);
    setUploading(true);

    try {
      let parsedData = [];
      let headers = [];

      if (fileExt === "csv") {
        const result = await new Promise((resolve, reject) => {
          Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => resolve(results),
            error: (error) => reject(error),
          });
        });

        headers = result.meta.fields || [];
        parsedData = result.data;
      } else if (fileExt === "xlsx") {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { defval: "" });

        if (jsonData.length > 0) {
          headers = Object.keys(jsonData[0]);
          parsedData = jsonData;
        }
      }

      if (!headers.length || parsedData.length === 0) {
        throw new Error("No columns or data found in file");
      }

      const dataWithIds = parsedData.map((row, idx) => ({ ...row, _tempId: idx }));

      setPreviewColumns(headers);
      setPreviewDataAll(dataWithIds);
      setOriginalPreviewData(JSON.parse(JSON.stringify(dataWithIds)));
      setPreviewCurrentPage(1);
      setEditingRowIndex(null);
      setShowPreviewModal(true);
    } catch (error) {
      console.error("Parse error:", error);
      Swal.fire({
        icon: "error",
        title: "Parse Failed",
        text: `Could not read file: ${error.message}`,
      });
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  }, []);

  const getCurrentPreviewRows = useCallback(() => {
    const start = (previewCurrentPage - 1) * previewRowsPerPage;
    const end = start + previewRowsPerPage;
    return previewDataAll.slice(start, end);
  }, [previewDataAll, previewCurrentPage, previewRowsPerPage]);

  const totalPreviewPages = Math.ceil(previewDataAll.length / previewRowsPerPage);

  const handlePreviewPageChange = (newPage) => {
    setPreviewCurrentPage(newPage);
    setEditingRowIndex(null);
  };

  const startEditing = (globalIndex) => {
    const rowData = previewDataAll[globalIndex];
    const { _tempId, ...editableData } = rowData;
    setEditFormData(editableData);
    setEditingRowIndex(globalIndex);
  };

  const cancelEditing = () => {
    setEditingRowIndex(null);
    setEditFormData({});
  };

  const handleEditFieldChange = (column, value) => {
    setEditFormData(prev => ({ ...prev, [column]: value }));
  };

  const saveEditedRow = () => {
    if (editingRowIndex !== null) {
      const updatedData = [...previewDataAll];
      updatedData[editingRowIndex] = { ...updatedData[editingRowIndex], ...editFormData };
      setPreviewDataAll(updatedData);
      setEditingRowIndex(null);
      setEditFormData({});
    }
  };

  const deleteRow = (globalIndex) => {
    Swal.fire({
      title: 'Delete Row',
      text: 'Are you sure you want to delete this row?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        const updatedData = [...previewDataAll];
        updatedData.splice(globalIndex, 1);
        setPreviewDataAll(updatedData);
        const newTotalPages = Math.ceil(updatedData.length / previewRowsPerPage);
        if (previewCurrentPage > newTotalPages && newTotalPages > 0) {
          setPreviewCurrentPage(newTotalPages);
        } else if (updatedData.length === 0) {
          setPreviewCurrentPage(1);
        }
        Swal.fire('Deleted!', 'Row has been deleted.', 'success');
      }
    });
  };

  const resetPreviewData = () => {
    Swal.fire({
      title: 'Reset Data',
      text: 'Reset all changes to original uploaded data?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, reset',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        setPreviewDataAll(JSON.parse(JSON.stringify(originalPreviewData)));
        setPreviewCurrentPage(1);
        setEditingRowIndex(null);
        Swal.fire('Reset', 'Data has been reset to original.', 'success');
      }
    });
  };

  const confirmUpload = useCallback(async () => {
    if (!selectedFile || !previewDataAll) return;
    const dataToUpload = previewDataAll.map(({ _tempId, ...rest }) => rest);
    try {
      setUploading(true);
      setShowPreviewModal(false);
      const fileExt = selectedFile.name.split(".").pop().toLowerCase();
      let blob;
      let fileName = selectedFile.name;
      if (fileExt === "csv") {
        const csvString = Papa.unparse(dataToUpload);
        blob = new Blob([csvString], { type: "text/csv" });
        fileName = "modified_lead_data.csv";
      } else {
        const ws = XLSX.utils.json_to_sheet(dataToUpload);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Leads");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        fileName = "modified_lead_data.xlsx";
      }

      const modifiedFile = new File([blob], fileName, { type: blob.type });

      let response;
      if (fileExt === "csv") {
        response = await uploadLeadCSV(modifiedFile);
      } else {
        response = await uploadLeadExcel(modifiedFile);
      }

      if (response?.success === false && response?.required_columns?.length > 0) {
        Swal.fire({
          icon: "error",
          title: "Missing Columns",
          html: `<div><b>Required columns missing:</b><br/>${response.required_columns.join("<br/>")}</div>`
        });
        return;
      }

      if (response?.success === false) {
        Swal.fire({ icon: "error", title: "Upload Failed", text: response?.message || "Something went wrong" });
        return;
      }


      Swal.fire({
        icon: "success",
        title: "Success",
        text: `${selectedFile.name} uploaded successfully with modifications!`,
        timer: 2000,
        showConfirmButton: false
      });
      dispatch(triggerRefresh("lead"));
     dispatch(closeModal());
    } catch (error) {
      Swal.fire({ icon: "error", title: "Upload Failed", text: error?.response?.data?.message || "Check console for more details." });
    } finally {
      setUploading(false);
      setSelectedFile(null);
      setPreviewDataAll([]);
      setPreviewColumns([]);
      setOriginalPreviewData([]);
      setEditingRowIndex(null);
    }
  }, [selectedFile, previewDataAll, dispatch]);



  if (showPreviewModal) {
    return (
      <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }} onClick={() => setShowPreviewModal(false)}>
        <div className="modal-dialog modal-xl modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <FaFileExcel className="me-2" />
                Data Preview - {selectedFile?.name}
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowPreviewModal(false)}></button>
            </div>
            <div className="modal-body">
              <div className="alert alert-info d-flex justify-content-between align-items-center">
                <div>
                  <strong>Total Records:</strong> {previewDataAll.length} rows &nbsp;|&nbsp;
                  <strong>Total Columns:</strong> {previewColumns.length}
                </div>
                <button className="add-btn" onClick={resetPreviewData}>
                  <RiResetLeftLine className="me-1" /> Reset to Original
                </button>
              </div>
              <div className="table-responsive">
                <table className="table table-bordered table-sm">
                  <thead>
                    <tr>
                      <th>#</th>
                      {previewColumns.map((col, idx) => (
                        <th key={idx}>{col}</th>
                      ))}
                      <th style={{ width: "120px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getCurrentPreviewRows().map((row, idx) => {
                      const globalIndex = (previewCurrentPage - 1) * previewRowsPerPage + idx;
                      const isEditing = editingRowIndex === globalIndex;
                      if (isEditing) {
                        return (
                          <tr key={row._tempId || globalIndex} className="table-active">
                            <td>{globalIndex + 1}</td>
                            {previewColumns.map((col) => (
                              <td key={col}>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={editFormData[col] !== undefined && editFormData[col] !== null ? String(editFormData[col]) : ""}
                                  onChange={(e) => handleEditFieldChange(col, e.target.value)}
                                />
                              </td>
                            ))}
                            <td>
                              <div className="d-flex">
                              <button className="btn btn-sm btn-success me-1" onClick={saveEditedRow} title="Save">
                                <FaSave />
                              </button>
                              <button className="btn btn-sm btn-secondary" onClick={cancelEditing} title="Cancel">
                                <FaTimesCircle />
                              </button>
                              </div>
                            </td>

                          </tr>
                        );
                      }
                      return (
                        <tr key={row._tempId || globalIndex}>
                          <td>{globalIndex + 1}</td>
                          {previewColumns.map((col) => (
                            <td key={col}>{row[col] !== undefined && row[col] !== null ? String(row[col]) : ""}</td>
                          ))}
                          <td>
                            <div className="d-flex">
                            <button     className="action-btn action-edit" onClick={() => startEditing(globalIndex)} title="Edit">
                              <FaEdit />
                            </button>
                            <button className="st-action-btn st-delete-btn" onClick={() => deleteRow(globalIndex)} title="Delete">
                              <FaTrash />
                            </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {getCurrentPreviewRows().length === 0 && (
                      <tr>
                        <td colSpan={previewColumns.length + 2} className="text-center">No data to display</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {totalPreviewPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    Page {previewCurrentPage} of {totalPreviewPages}
                  </div>
                  <div className="btn-group">
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => handlePreviewPageChange(previewCurrentPage - 1)}
                      disabled={previewCurrentPage === 1}
                    >
                      <FaArrowLeft /> Previous
                    </button>
                    <button
                      className="btn btn-info btn-sm"
                      onClick={() => handlePreviewPageChange(previewCurrentPage + 1)}
                      disabled={previewCurrentPage === totalPreviewPages}
                    >
                      Next <FaArrowRight />
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="add-btn" onClick={() => setShowPreviewModal(false)}>
                <FaTimes className="me-1" /> Cancel
              </button>
              <button className="add-btn" onClick={confirmUpload} disabled={uploading}>
                {uploading ? "Uploading..." : "Confirm & Upload"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1040 }} onClick={handleClose}>
      <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title"><FaUpload className="me-2" /> Import Leads</h5>
            <button type="button" className="btn-close" onClick={handleClose}></button>
          </div>
          <div className="modal-body">
            <p>Download a template or upload your CSV/Excel file.</p>
            <div className="d-grid gap-3">
              <div className="d-flex gap-2">
                <button className="btn btn-outline-primary w-50" onClick={downloadCSVTemplate}>
                  <FaFileCsv className="me-2" /> CSV Template
                </button>
                <button className="btn btn-outline-success w-50" onClick={downloadExcelTemplate}>
                  <FaFileExcel className="me-2" /> Excel Template
                </button>
              </div>
              <div className="border rounded p-3 text-center">
                <FaUpload className="fs-1 mb-2" style={{ color: "#6c757d" }} />
                <p className="mb-2">Upload your file (.csv or .xlsx)</p>
                <label className="add-btn">
                  {uploading ? "Processing..." : "Choose File"}
                  <input
                    type="file"
                    accept=".csv, .xlsx"
                    onChange={handleFileSelect}
                    disabled={uploading}
                    style={{ display: "none" }}
                  />
                </label>
                {uploading && <div className="mt-2 text-muted">Processing file, please wait...</div>}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-danger" onClick={handleClose}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadPreviewModal;